/**
 * ZBufferEngine.ts
 *
 * CPU-based Z-Buffer / Depth Map engine for CNC stock simulation.
 * Uses 6 orthographic depth views (+X,-X,+Y,-Y,+Z,-Z) to represent stock material.
 * This is the industry-standard approach used by Vericut/NCSimul.
 *
 * Architecture:
 * - Each view stores two depth maps: nearDepth (closest surface) and farDepth (farthest surface)
 * - Resolution computed from bbox and voxelSize (same as VoxelCSGEngine)
 * - Memory: ~12 MB for 500x500 resolution per view
 *
 * Performance characteristics:
 * - Fast material removal (direct depth map updates, O(tool_area))
 * - Efficient memory usage (6 depth maps vs full 3D voxel grid)
 * - Industry-proven for CNC simulation
 */

import * as THREE from 'three';
import { VoxelToolGeometry, VoxelBBox } from './VoxelCSGEngine';

/**
 * Represents one orthographic depth view of the stock material
 */
export interface DepthView {
  direction: '+X' | '-X' | '+Y' | '-Y' | '+Z' | '-Z';
  axisIdx: number;        // 0=X, 1=Y, 2=Z
  sign: number;           // +1 or -1
  width: number;          // pixels in U direction
  height: number;         // pixels in V direction
  nearDepth: Float32Array; // nearest surface distance from view plane
  farDepth: Float32Array;  // farthest surface distance from view plane
  pixelSize: number;      // mm per pixel
  // View plane origin (world coords of pixel 0,0)
  uOrigin: number;
  vOrigin: number;
  depthOrigin: number;    // starting depth value
  depthEnd: number;       // ending depth value
}

/**
 * Z-Buffer based stock simulation engine
 *
 * Uses 6 orthographic depth views to represent stock material.
 * Each view stores near and far depth values for efficient material removal.
 */
export class ZBufferEngine {
  readonly bbox: VoxelBBox;
  readonly voxelSize: number;
  private views: DepthView[] = [];
  private disposed = false;

  constructor(bbox: VoxelBBox, voxelSize: number) {
    this.bbox = bbox;
    this.voxelSize = voxelSize;
    this.initializeViews();
  }

  /**
   * Initialize the 6 orthographic depth views
   */
  private initializeViews(): void {
    const { minX, minY, minZ, maxX, maxY, maxZ } = this.bbox;

    // Define the 6 views
    const viewConfigs: Array<{
      direction: DepthView['direction'];
      axisIdx: number;
      sign: number;
      uAxis: number;  // 0=X, 1=Y, 2=Z
      vAxis: number;
      depthAxis: number;
    }> = [
      { direction: '+X', axisIdx: 0, sign: 1, uAxis: 1, vAxis: 2, depthAxis: 0 },
      { direction: '-X', axisIdx: 0, sign: -1, uAxis: 1, vAxis: 2, depthAxis: 0 },
      { direction: '+Y', axisIdx: 1, sign: 1, uAxis: 0, vAxis: 2, depthAxis: 1 },
      { direction: '-Y', axisIdx: 1, sign: -1, uAxis: 0, vAxis: 2, depthAxis: 1 },
      { direction: '+Z', axisIdx: 2, sign: 1, uAxis: 0, vAxis: 1, depthAxis: 2 },
      { direction: '-Z', axisIdx: 2, sign: -1, uAxis: 0, vAxis: 1, depthAxis: 2 },
    ];

    const bboxDims = [maxX - minX, maxY - minY, maxZ - minZ];
    const origins = [minX, minY, minZ];
    const maxs = [maxX, maxY, maxZ];

    for (const config of viewConfigs) {
      const { direction, axisIdx, sign, uAxis, vAxis, depthAxis } = config;

      // Calculate resolution for U and V axes
      const width = Math.ceil(bboxDims[uAxis] / this.voxelSize);
      const height = Math.ceil(bboxDims[vAxis] / this.voxelSize);
      const pixelSize = this.voxelSize;

      // Calculate depth range
      // depthOrigin = starting depth (view origin side)
      // depthEnd = ending depth (far side of stock)
      // For +Z: depthOrigin=minZ, depthEnd=maxZ (nearDepth starts at minZ, increases as cut)
      // The mesh uses farDepth as the visible top surface
      const depthOrigin = sign > 0 ? origins[depthAxis] : maxs[depthAxis];
      const depthEnd = sign > 0 ? maxs[depthAxis] : origins[depthAxis];

      // Create depth buffers
      const bufferSize = width * height;
      const nearDepth = new Float32Array(bufferSize);
      const farDepth = new Float32Array(bufferSize);

      const view: DepthView = {
        direction,
        axisIdx,
        sign,
        width,
        height,
        nearDepth,
        farDepth,
        pixelSize,
        uOrigin: origins[uAxis],
        vOrigin: origins[vAxis],
        depthOrigin,
        depthEnd,
      };

      this.views.push(view);
    }
  }

  /**
   * Initialize as a full rectangular stock
   * Sets all depth maps to represent a solid block
   */
  initializeRectangularStock(): void {
    for (const view of this.views) {
      const bufferSize = view.width * view.height;

      // Near depth starts at the view plane (camera position)
      view.nearDepth.fill(view.depthOrigin);

      // Far depth is at the opposite face of the bbox
      view.farDepth.fill(view.depthEnd);
    }
  }

  /**
   * Remove material using a tool at a specific position
   *
   * @param x Tool center X
   * @param y Tool center Y
   * @param z Tool center Z (tip position for vertical tools)
   * @param tool Tool geometry
   * @param dirX Tool direction X component
   * @param dirY Tool direction Y component
   * @param dirZ Tool direction Z component
   * @returns Volume removed in mm³
   */
  removeMaterial(
    x: number,
    y: number,
    z: number,
    tool: VoxelToolGeometry,
    dirX: number,
    dirY: number,
    dirZ: number
  ): number {
    if (this.disposed) return 0;

    let volumeRemoved = 0;

    // Determine primary axis (tool direction)
    const absX = Math.abs(dirX);
    const absY = Math.abs(dirY);
    const absZ = Math.abs(dirZ);
    const primaryAxis = absX > absY ? (absX > absZ ? 0 : 2) : (absY > absZ ? 1 : 2);

    // For now, focus on Z-axis tools (most common in CNC)
    if (primaryAxis === 2 && Math.abs(dirZ) > 0.9) {
      volumeRemoved += this.removeVerticalTool(x, y, z, tool, dirZ > 0 ? 1 : -1);
    } else {
      // For non-vertical tools, approximate with projection
      volumeRemoved += this.removeGeneralTool(x, y, z, tool, dirX, dirY, dirZ);
    }

    return volumeRemoved;
  }

  /**
   * Remove material swept along a line segment
   */
  removeSweptMaterial(
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    tool: VoxelToolGeometry,
    dirX: number, dirY: number, dirZ: number
  ): number {
    if (this.disposed) return 0;

    // Sample along the line segment
    const dx = bx - ax;
    const dy = by - ay;
    const dz = bz - az;
    const length = Math.sqrt(dx * dx + dy * dy + dz * dz);

    if (length < 0.001) {
      return this.removeMaterial(ax, ay, az, tool, dirX, dirY, dirZ);
    }

    // Sample at voxelSize intervals
    const steps = Math.max(2, Math.ceil(length / this.voxelSize));
    let totalVolume = 0;

    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const px = ax + dx * t;
      const py = ay + dy * t;
      const pz = az + dz * t;

      totalVolume += this.removeMaterial(px, py, pz, tool, dirX, dirY, dirZ);
    }

    return totalVolume;
  }

  /**
   * Remove material using a vertical tool (Z-axis aligned)
   */
  private removeVerticalTool(
    x: number,
    y: number,
    z: number,
    tool: VoxelToolGeometry,
    sign: number
  ): number {
    let volumeRemoved = 0;
    const radius = tool.diameter / 2;
    const length = tool.length;

    // Get +Z and -Z views
    const viewZ = this.views.find(v => v.direction === (sign > 0 ? '+Z' : '-Z'));
    if (!viewZ) return 0;

    // Convert world coords to pixel coords
    const pixelX = Math.floor((x - viewZ.uOrigin) / viewZ.pixelSize);
    const pixelY = Math.floor((y - viewZ.vOrigin) / viewZ.pixelSize);

    // Calculate pixel radius
    const pixelRadius = Math.ceil(radius / viewZ.pixelSize);

    // Tool depth position
    const toolDepth = sign > 0 ? z : z;
    const toolEnd = sign > 0 ? z + length : z - length;

    // Update depth map in circular region
    for (let dy = -pixelRadius; dy <= pixelRadius; dy++) {
      for (let dx = -pixelRadius; dx <= pixelRadius; dx++) {
        const px = pixelX + dx;
        const py = pixelY + dy;

        // Bounds check
        if (px < 0 || px >= viewZ.width || py < 0 || py >= viewZ.height) continue;

        // Check if within tool radius
        const dist = Math.sqrt(dx * dx + dy * dy) * viewZ.pixelSize;
        if (dist > radius) continue;

        const idx = py * viewZ.width + px;
        const oldNear = viewZ.nearDepth[idx];
        const oldFar = viewZ.farDepth[idx];

        // Update near depth (tool pushes surface deeper)
        if (sign > 0) {
          const newNear = Math.max(oldNear, toolEnd);
          if (newNear > oldNear) {
            const depth = Math.min(newNear, oldFar) - oldNear;
            volumeRemoved += depth * viewZ.pixelSize * viewZ.pixelSize;
            viewZ.nearDepth[idx] = newNear;
          }
        } else {
          const newFar = Math.min(oldFar, toolEnd);
          if (newFar < oldFar) {
            const depth = oldFar - Math.max(newFar, oldNear);
            volumeRemoved += depth * viewZ.pixelSize * viewZ.pixelSize;
            viewZ.farDepth[idx] = newFar;
          }
        }

        // Check if fully cut through
        if (viewZ.nearDepth[idx] >= viewZ.farDepth[idx]) {
          viewZ.nearDepth[idx] = viewZ.farDepth[idx];
        }
      }
    }

    // Update perpendicular views (X and Y)
    this.updatePerpendicularViews(x, y, z, radius, length, sign);

    return volumeRemoved;
  }

  /**
   * Update X and Y views for a vertical tool
   */
  private updatePerpendicularViews(
    x: number,
    y: number,
    z: number,
    radius: number,
    length: number,
    signZ: number
  ): void {
    const toolTop = signZ > 0 ? z + length : z;
    const toolBottom = signZ > 0 ? z : z - length;

    // Update +X and -X views
    for (const view of this.views) {
      if (view.direction === '+X' || view.direction === '-X') {
        this.updateSideView(view, y, z, radius, toolTop, toolBottom, 0);
      } else if (view.direction === '+Y' || view.direction === '-Y') {
        this.updateSideView(view, x, z, radius, toolTop, toolBottom, 1);
      }
    }
  }

  /**
   * Update a side view with tool projection
   */
  private updateSideView(
    view: DepthView,
    centerU: number,
    centerV: number,
    radius: number,
    vTop: number,
    vBottom: number,
    primaryUAxis: number
  ): void {
    // U is the horizontal axis (Y for X views, X for Y views)
    // V is always Z for side views
    const pixelU = Math.floor((centerU - view.uOrigin) / view.pixelSize);
    const pixelVTop = Math.floor((vTop - view.vOrigin) / view.pixelSize);
    const pixelVBottom = Math.floor((vBottom - view.vOrigin) / view.pixelSize);
    const pixelRadius = Math.ceil(radius / view.pixelSize);

    const vMin = Math.min(pixelVTop, pixelVBottom);
    const vMax = Math.max(pixelVTop, pixelVBottom);

    for (let dv = vMin; dv <= vMax; dv++) {
      for (let du = -pixelRadius; du <= pixelRadius; du++) {
        const pu = pixelU + du;
        const pv = dv;

        if (pu < 0 || pu >= view.width || pv < 0 || pv >= view.height) continue;

        const idx = pv * view.width + pu;

        // Tool projects as a rectangle in side view
        // This is a simplified approximation
        const toolDepth = centerU + (du > 0 ? radius : -radius);

        if (view.sign > 0) {
          view.nearDepth[idx] = Math.max(view.nearDepth[idx], toolDepth);
        } else {
          view.farDepth[idx] = Math.min(view.farDepth[idx], toolDepth);
        }
      }
    }
  }

  /**
   * Remove material using a general-direction tool (fallback)
   */
  private removeGeneralTool(
    x: number,
    y: number,
    z: number,
    tool: VoxelToolGeometry,
    dirX: number,
    dirY: number,
    dirZ: number
  ): number {
    // Simplified: treat as vertical tool for now
    // A full implementation would project the tool cylinder onto each view
    return this.removeVerticalTool(x, y, z, tool, dirZ > 0 ? 1 : -1);
  }

  /**
   * Generate a 3D mesh from the depth maps
   *
   * Strategy: Create a closed box mesh using top and bottom heightfields
   * - Top surface from +Z farDepth (initially maxZ, decreases as cut from above)
   * - Bottom surface from +Z nearDepth (initially minZ, increases as cut from below)
   * - Side walls where material exists at the boundary
   */
  generateMesh(): THREE.BufferGeometry {
    const viewZ = this.views.find(v => v.direction === '+Z');
    if (!viewZ) {
      return new THREE.BufferGeometry();
    }

    const { width, height, nearDepth, farDepth, pixelSize, uOrigin, vOrigin } = viewZ;

    const vertices: number[] = [];
    const indices: number[] = [];

    // Create top surface vertex grid (using farDepth = top surface height)
    // Also create bottom surface at nearDepth for a proper closed mesh
    const topBase = 0;
    const botBase = width * height;

    for (let j = 0; j < height; j++) {
      for (let i = 0; i < width; i++) {
        const idx = j * width + i;
        const x = uOrigin + (i + 0.5) * pixelSize;
        const y = vOrigin + (j + 0.5) * pixelSize;

        // Top surface vertex
        vertices.push(x, y, farDepth[idx]);
        // Bottom surface vertex
        vertices.push(x, y, nearDepth[idx]);
      }
    }

    // Create top surface triangles (winding for +Z normal)
    for (let j = 0; j < height - 1; j++) {
      for (let i = 0; i < width - 1; i++) {
        const idx = j * width + i;
        // Only generate triangles where material exists
        const hasA = nearDepth[idx] < farDepth[idx] - 0.001;
        const hasB = nearDepth[idx + 1] < farDepth[idx + 1] - 0.001;
        const hasC = nearDepth[idx + width] < farDepth[idx + width] - 0.001;
        const hasD = nearDepth[idx + width + 1] < farDepth[idx + width + 1] - 0.001;

        // Top surface: indices into first half of vertices (even indices = top)
        const ta = idx * 2;
        const tb = (idx + 1) * 2;
        const tc = (idx + width) * 2;
        const td = (idx + width + 1) * 2;

        if (hasA && hasB && hasC) {
          indices.push(ta, tb, tc);
        }
        if (hasB && hasD && hasC) {
          indices.push(tb, td, tc);
        }

        // Bottom surface: indices into second half (odd indices = bottom)
        const ba = ta + 1;
        const bb = tb + 1;
        const bc = tc + 1;
        const bd = td + 1;

        if (hasA && hasB && hasC) {
          indices.push(ba, bc, bb); // Reversed winding for -Z normal
        }
        if (hasB && hasD && hasC) {
          indices.push(bb, bc, bd);
        }
      }
    }

    // Create geometry
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    return geometry;
  }

  /**
   * Reset to initial state
   */
  reset(): void {
    this.initializeRectangularStock();
  }

  /**
   * Set depth data from GPU readback (for GPU→CPU mesh generation path)
   * @param allDepths Array of {near, far} Float32Arrays, one per view (6 total)
   */
  setDepthDataFromGPU(allDepths: Array<{ near: Float32Array; far: Float32Array }>): void {
    const count = Math.min(allDepths.length, this.views.length);
    for (let i = 0; i < count; i++) {
      const view = this.views[i];
      const gpu = allDepths[i];
      const size = view.width * view.height;
      // Copy GPU data into CPU view buffers (same layout)
      view.nearDepth.set(gpu.near.subarray(0, size));
      view.farDepth.set(gpu.far.subarray(0, size));
    }
  }

  /**
   * Dispose of resources
   */
  dispose(): void {
    if (this.disposed) return;

    // Clear all depth buffers
    for (const view of this.views) {
      // TypeScript doesn't have explicit memory free, but clear references
      (view.nearDepth as any) = null;
      (view.farDepth as any) = null;
    }

    this.views = [];
    this.disposed = true;
  }

  /**
   * Get statistics about the engine
   */
  getStats(): {
    totalPixels: number;
    memoryUsage: number;
    viewCount: number;
    resolution: { width: number; height: number }[];
  } {
    let totalPixels = 0;
    const resolution: { width: number; height: number }[] = [];

    for (const view of this.views) {
      const pixels = view.width * view.height;
      totalPixels += pixels;
      resolution.push({ width: view.width, height: view.height });
    }

    // Each pixel has 2 floats (near + far), each float is 4 bytes
    const memoryUsage = totalPixels * 2 * 4;

    return {
      totalPixels,
      memoryUsage,
      viewCount: this.views.length,
      resolution,
    };
  }
}

/**
 * Factory function to create a Z-Buffer engine
 */
export function createZBufferEngine(bbox: VoxelBBox, voxelSize: number): ZBufferEngine {
  const engine = new ZBufferEngine(bbox, voxelSize);
  engine.initializeRectangularStock();
  return engine;
}
